import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Divider,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { format, formatDistanceToNow } from 'date-fns';
import { useMessagePolling } from '../../hooks/useMessagePolling';

interface User {
  id: string;
  name: string;
  email: string;
}

interface Message {
  id: number;
  content: string;
  sender: {
    id: string;
    name: string;
    type: 'user' | 'admin';
  };
  createdAt: string;
  isRead: boolean;
}

interface Thread {
  id: number;
  subject: string;
  category: string | null;
  status: 'open' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  createdBy: User;
  createdAt: string;
}

interface Participant {
  id: string;
  name: string;
  type: 'customer' | 'admin' | 'observer';
}

interface ThreadResponse {
  success: boolean;
  data?: {
    thread: Thread;
    messages: Message[];
    participants: Participant[];
  };
}

const MessageThreadPage: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messagesEndRef = useRef<any>(null);
  const messageContainerRef = useRef<any>(null);

  // Enable polling for this specific thread
  useMessagePolling({
    enabled: true,
    interval: 15000, // 15 seconds for active thread
    onMessagesPage: false,
    currentThreadId: threadId ? parseInt(threadId) : undefined,
  });

  // Fetch thread details and messages
  const { data: threadData, isLoading, error } = useQuery({
    queryKey: ['messages', 'thread', threadId],
    queryFn: async () => {
      if (!threadId) throw new Error('Thread ID is required');

      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/threads/${threadId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response?.ok) {
        if (response?.status === 404) {
          throw new Error('Thread not found');
        }
        throw new Error('Failed to fetch thread');
      }

      const data: ThreadResponse = await response.json();
      return data;
    },
    staleTime: 5000, // Consider data stale after 5 seconds
  });

  // Mark thread as read
  const markAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/threads/${threadId}/read`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to mark as read');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate unread count
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
      // Invalidate thread list to update unread badges
      queryClient.invalidateQueries({ queryKey: ['messages', 'threads'] });
    },
  });

  // Send reply mutation
  const sendReplyMutation = useMutation({
    mutationFn: async (content: string) => {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/threads/${threadId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content }),
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to send reply');
      }

      return response.json();
    },
    onSuccess: () => {
      setReplyContent('');
      // Refetch thread messages
      queryClient.invalidateQueries({ queryKey: ['messages', 'thread', threadId] });
      // Update thread list
      queryClient.invalidateQueries({ queryKey: ['messages', 'threads'] });
    },
  });

  // Mark as read when thread is loaded
  useEffect(() => {
    if (threadData?.data && threadId) {
      const hasUnreadMessages = threadData.data.messages.some(msg => !msg.isRead);
      if (hasUnreadMessages) {
        markAsReadMutation.mutate();
      }
    }
  }, [threadData, threadId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [threadData?.data?.messages]);

  const handleSendReply = async () => {
    if (!replyContent.trim()) return;

    setIsSubmitting(true);
    try {
      await sendReplyMutation.mutateAsync(replyContent);
    } catch (error) {
      console.error('Failed to send reply:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'info';
      case 'pending':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'primary';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {error instanceof Error && error.message === 'Thread not found' 
            ? 'This conversation could not be found.'
            : 'Failed to load conversation. Please try again later.'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/messages')}
          sx={{ mt: 2 }}
        >
          Back to Messages
        </Button>
      </Container>
    );
  }

  const thread = threadData?.data?.thread;
  const messages = threadData?.data?.messages || [];

  const isThreadClosed = thread?.status === 'closed' || thread?.status === 'resolved';

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate('/messages')}
            sx={{ cursor: 'pointer' }}
          >
            Messages
          </Link>
          <Typography color="text.primary" variant="body2">
            {thread?.subject}
          </Typography>
        </Breadcrumbs>

        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton onClick={() => navigate('/messages')} edge="start">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" gutterBottom>
              {thread?.subject}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={thread?.status}
                size="small"
                color={getStatusColor(thread?.status || '') as any}
              />
              {thread?.priority !== 'normal' && (
                <Chip
                  label={thread?.priority}
                  size="small"
                  color={getPriorityColor(thread?.priority || '') as any}
                  variant="outlined"
                />
              )}
              {thread?.category && (
                <Chip
                  label={thread?.category}
                  size="small"
                  variant="outlined"
                />
              )}
              <Typography variant="caption" color="text.secondary">
                Started {thread?.createdAt && formatDistanceToNow(new Date(thread.createdAt), { addSuffix: true })}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* Messages */}
      <Paper sx={{ mb: 3 }}>
        <Box
          ref={messageContainerRef}
          sx={{
            maxHeight: '500px',
            overflowY: 'auto',
            p: 2,
          }}
        >
          {messages.map((message, index) => (
            <Box key={message.id}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Avatar sx={{ bgcolor: message.sender.type === 'admin' ? 'secondary.main' : 'primary.main' }}>
                  {message.sender.type === 'admin' ? <SupportIcon /> : <PersonIcon />}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2">
                      {message.sender.name}
                    </Typography>
                    {message.sender.type === 'admin' && (
                      <Chip
                        label="Support Team"
                        size="small"
                        color="secondary"
                        sx={{ height: 20 }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(message.createdAt), 'MMM d, yyyy h:mm a')}
                    </Typography>
                  </Stack>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word' 
                    }}
                  >
                    {message.content}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>
      </Paper>

      {/* Reply Box */}
      {!isThreadClosed ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Reply to this conversation
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Type your message here..."
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isSubmitting}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              onClick={handleSendReply}
              disabled={!replyContent.trim() || isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Reply'}
            </Button>
          </Box>
        </Paper>
      ) : (
        <Alert severity="info">
          This conversation has been {thread?.status}. To continue the discussion, please start a new conversation.
        </Alert>
      )}
    </Container>
  );
};

export default MessageThreadPage;